import { IconAt, IconMail } from '@developerz/ui';
import { createSignal, createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  grantable,
  type InviteTarget,
  inviteMember,
  type OrgInvitationMint,
  type OrgRole,
} from '../lib/org-members';
import { InvitationMintNotice } from './InvitationMintNotice';
import { Select } from './Select';
import { Button } from './ui/Button';

// Invite row (#308): leading-icon address input (spec §5 .search-input
// pattern), a role picker, primary .btn. Presentational — the parent's
// `run` owns the mutation, busy/error, and refetch. The address field is
// uncontrolled: read at submit, kept out of the render path.
//
// TWO ADDRESSING CHANNELS (arc 101 slice 01): a GitHub login reaches someone who
// has already signed in, an email reaches anyone. The four rules that shape this
// form — picked-not-sniffed, resend-is-the-same-call, resend at the invitation's
// role, and the link shown once — are in `apps/dashboard/CLAUDE.md`.

/** Which channel the address field is addressing. */
type InviteMode = 'login' | 'email';

export function InviteForm(props: {
  readonly actor: OrgRole;
  readonly busy: boolean;
  readonly token: string | null | undefined;
  readonly run: (fn: () => Promise<void>, successMsg?: string) => Promise<void>;
}): JSX.Element {
  const roleId = createUniqueId();
  const [inviteRole, setInviteRole] = createSignal<OrgRole>('member');
  const [mode, setMode] = createSignal<InviteMode>('login');
  const [minted, setMinted] = createSignal<OrgInvitationMint | null>(null);
  let addressInput: HTMLInputElement | undefined;

  const email = (): boolean => mode() === 'email';

  // Switching channel clears the field: a GitHub login is not an email, so a
  // half-typed one carried across could only be refused.
  const pick = (next: InviteMode): void => {
    if (mode() === next) return;
    setMode(next);
    if (addressInput) addressInput.value = '';
  };

  /**
   * The ONE invite call — and therefore the one resend. `role` is a parameter
   * rather than a read of `inviteRole()` precisely so resend can pass the
   * invitation's OWN role: a rotation UPDATEs `invitations.role`, so re-arming
   * off the live selector would silently re-rank an invitation already out.
   */
  const send = (target: InviteTarget, role: OrgRole, onSent?: () => void): void => {
    void props.run(
      async () => {
        const result = await inviteMember(target, role, props.token);
        // The route's two success shapes are not interchangeable: `invited` means
        // nothing is true of the roster yet, so it gets a notice that persists
        // (it carries the accept link) rather than a toast that expires.
        setMinted(result.outcome === 'invited' ? result.invitation : null);
        onSent?.();
      },
      target.by === 'login' ? t('org_members.toast_invited') : undefined,
    );
  };

  const invite = (e?: Event): void => {
    e?.preventDefault();
    const address = (addressInput?.value ?? '').trim();
    if (!address) return;
    send(
      email() ? { by: 'email', email: address } : { by: 'login', login: address },
      inviteRole(),
      () => {
        if (addressInput) addressInput.value = '';
      },
    );
  };

  return (
    <>
      {/* A <fieldset>, not a `role="group"` div: the grouping is native, and the
          sr-only <legend> is what names it — `<RunTimelineFilters/>`'s shape. */}
      <fieldset class="org-members__invite-modes">
        <legend class="sr-only">{t('org_members.invite_mode')}</legend>
        <Button
          variant="outline"
          size="sm"
          data-mode="login"
          aria-pressed={email() ? 'false' : 'true'}
          onClick={() => pick('login')}
        >
          {t('org_members.invite_mode_login')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-mode="email"
          aria-pressed={email() ? 'true' : 'false'}
          onClick={() => pick('email')}
        >
          {t('org_members.invite_mode_email')}
        </Button>
      </fieldset>

      <form class="org-members__invite" onSubmit={invite}>
        <div class="search-input">
          {email() ? <IconMail /> : <IconAt />}
          <input
            ref={addressInput}
            type={email() ? 'email' : 'text'}
            class="input"
            placeholder={t(
              email() ? 'org_members.invite_email_placeholder' : 'org_members.invite_placeholder',
            )}
            aria-label={t(email() ? 'org_members.invite_email' : 'org_members.invite_login')}
            autocomplete="off"
            autocapitalize="none"
            spellcheck={false}
          />
        </div>
        <span class="org-members__invite-role">
          <label id={`${roleId}-label`} for={roleId} class="sr-only">
            {t('org_members.invite_role')}
          </label>
          <Select<OrgRole>
            a11y={{ id: roleId }}
            label={t('org_members.invite_role')}
            block
            value={inviteRole()}
            options={grantable(props.actor).map((r) => ({
              value: r,
              label: t(`org_members.role_${r}`),
            }))}
            onChange={setInviteRole}
          />
        </span>
        <Button disabled={props.busy} onClick={() => invite()}>
          {t('org_members.send_invite')}
        </Button>
      </form>

      <Show when={minted()}>
        {(mint) => (
          <InvitationMintNotice
            mint={mint()}
            busy={props.busy}
            onResend={() => send({ by: 'email', email: mint().email }, mint().role)}
            onDismiss={() => setMinted(null)}
          />
        )}
      </Show>
    </>
  );
}
